import {CSSProperties} from "react";
import {useApp} from "../app.context";

// Either flat options or grouped (with section headers via <optgroup>).
// Mutually exclusive — pass one, not both.
export interface SelectableGroup<T> {
    label: string;
    options: T[];
}

export interface SelectableProps<T> {
    value: T;
    options?: T[];
    groups?: SelectableGroup<T>[];
    onChange(value: T): void;
    style?: CSSProperties;
    // Render option values as-is instead of treating them as localization
    // keys. Use for values that aren't loc keys (branch names, enum values)
    // — otherwise they render as missing-key markers ([[...]]).
    literalOptions?: boolean;
    // Shown, unselectable, while the value is unset. Without one an unset picker
    // would show the first real option, and choosing that option would be no change.
    placeholder?: string;
}

// Controlled: the select always shows the value it is given, so it cannot drift
// from the data when that changes by another route (undo, a branch reload).
// Every caller's onChange must therefore write the value it passes back in.
function Selectable<T extends string | number>(props: SelectableProps<T>) {
    const {getLocalizationValue} = useApp();
    const label = (id: T) => props.literalOptions ? id.toString() : getLocalizationValue(id.toString());
    const value = props.value ?? '';
    const listed = (props.groups ? props.groups.flatMap(g => g.options) : props.options ?? []).some(o => o === value);
    // A value missing from the list (unset, or naming something since removed)
    // gets its own entry rather than silently showing the first option.
    const unlisted = !listed;

    return (
        <select style={{...{width: '200px'}, ...props.style}} value={value} onChange={(v) => {
            props.onChange(v.target.value as T);
        }}>
            {unlisted &&
                <option value={value} disabled hidden>{value === '' ? props.placeholder ?? '' : value.toString()}</option>}
            {props.groups
                ? props.groups.map((group, gi) => (
                    <optgroup key={gi} label={group.label}>
                        {group.options.map((id, index) => (
                            <option key={index} value={id}>{label(id)}</option>
                        ))}
                    </optgroup>
                ))
                : (props.options ?? []).map((id, index) => (
                    <option key={index} value={id}>{label(id)}</option>
                ))}
        </select>
    )
}

export default Selectable;